import React from 'react';
import { Button, Caption1, Text } from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { IStorefrontSection } from '../../Api/Models/Products';
import { ISingleProductContent } from '../Helpers/contentSectionConfig';
import { ProductImage } from '../Helpers/ProductImage';
import CurrencyFormat from '../Helpers/CurrencyFormat';
import QuotePrice from '../Product/QuotePrice';
import { useIsNarrow } from '../Helpers/useIsNarrow';
import { isNarrowFractionalWidth } from './SectionRowLayout';
import { FALLBACK_IMAGE } from '../../Constants/FallbackImage';
import useStyles from '../../styles';

interface SingleProductSectionProps {
    section: IStorefrontSection;
    config: ISingleProductContent | null;
}

// Multi-line clamp — keeps the hero's vertical rhythm predictable no matter how long the merchant's
// name/blurb is. Cast because csstype's WebkitBoxOrient union isn't inferred from an object literal.
const clamp = (lines: number): React.CSSProperties => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
});

// A single featured item (product / bundle / saved-search facade) rendered as a hero card that clicks
// through to its details page. The item is ALREADY RESOLVED by the BFF as section.products[0]; the
// shopper never fetches it. When there is no product (the server drops unresolvable ones) the section
// renders nothing. The section chrome (padding / title / styleProps background) is applied by the
// parent ContentSection — this component only renders the hero body and inherits text color so a
// merchant-set section background stays legible.
const SingleProductSection: React.FC<SingleProductSectionProps> = ({ section, config }) => {
    const styles = useStyles();
    const nav = useNavigate();
    const [hover, setHover] = React.useState(false);
    const isNarrow = useIsNarrow(700);

    const product = section.products?.[0];
    if (!product) return null;

    // Vertical card when the box is intrinsically narrow (a fractional column of 1/3 or less —
    // 'quarter' / 'third') or the viewport is narrow (everything stacks full-width); horizontal hero
    // otherwise ('half' and wider keep the side-by-side layout).
    const vertical = isNarrowFractionalWidth(section.width) || isNarrow;

    // Exact ProductCard routing switch: saved-search facade → dynamicProducts, else bundle, else product.
    const navigateToItem = () => {
        if (product.savedSearchId) nav(`/dynamicProducts/${product.savedSearchId}`);
        else if (product.bundleId) nav(`/bundles/${product.bundleId}`);
        else nav(`/products/${product.id}`);
    };

    const image = product.image && product.image !== '' ? product.image : FALLBACK_IMAGE;
    const ctaLabel = config?.ctaLabel || 'View Details';

    const heroStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        gap: vertical ? 0 : '1.25em',
        alignItems: 'stretch',
        border: '1px solid rgba(127,127,127,0.25)',
        borderRadius: 8,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        boxShadow: hover ? '0 6px 20px rgba(0,0,0,0.16)' : '0 1px 4px rgba(0,0,0,0.08)',
        transform: hover ? 'translateY(-2px)' : 'none',
    };

    const imagePaneStyle: React.CSSProperties = {
        position: 'relative',
        flex: vertical ? '0 0 auto' : '0 0 40%',
        width: vertical ? '100%' : undefined,
        minWidth: vertical ? undefined : 180,
        minHeight: vertical ? 180 : 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
        boxSizing: 'border-box',
        backgroundColor: 'white',
    };

    return (
        <div
            style={heroStyle}
            role="link"
            tabIndex={0}
            aria-label={product.name ?? ctaLabel}
            onClick={navigateToItem}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    navigateToItem();
                }
            }}
        >
            <div style={imagePaneStyle}>
                {/* BUNDLE flag — matches BundleCard's featured-flag styling. */}
                {product.bundleId && (
                    <div className={styles.featuredFlag} style={{ position: 'absolute', top: 0, left: 0 }}>
                        <Text weight="semibold" className={styles.featuredText}>BUNDLE</Text>
                    </div>
                )}
                <ProductImage
                    className={styles.smallRadius}
                    src={image}
                    alt={product.name ?? 'Featured product'}
                    fit="contain"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                />
            </div>

            {/* Info column. No hardcoded text colors — inherit the section's text color; secondary text
                is dimmed with opacity so it stays legible on any merchant background. */}
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75em',
                    padding: vertical ? '1em' : '1.25em 1.25em 1.25em 0',
                    justifyContent: 'center',
                }}
            >
                {product.manufacturer && (
                    <span style={{ fontSize: '0.75em', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.7 }}>
                        {product.manufacturer}
                    </span>
                )}

                <Text weight="semibold" style={{ fontSize: '1.4em', lineHeight: 1.2, ...clamp(vertical ? 3 : 2) }}>
                    {product.name}
                </Text>

                {product.hidePrice ? (
                    <Caption1 className={styles.caption} style={{ fontSize: '1.15em', fontWeight: 600 }}>
                        {/* A saved-search facade has no concrete item to quote yet, so its placeholder stays text. */}
                        <QuotePrice
                            itemId={product.bundleId ?? (product.savedSearchId ? undefined : product.id)}
                            itemType={product.bundleId ? 'bundle' : 'product'}
                            itemName={product.name}
                        />
                    </Caption1>
                ) : product.price ? (
                    <Caption1 className={styles.caption} style={{ fontSize: '1.25em', fontWeight: 600 }}>
                        <CurrencyFormat value={product.price} />
                    </Caption1>
                ) : null}

                {config?.blurb && (
                    <span style={{ opacity: 0.8, lineHeight: 1.4, ...clamp(3) }}>{config.blurb}</span>
                )}

                <div>
                    <Button
                        appearance="primary"
                        // Stop propagation so the CTA doesn't double-fire the card's navigate.
                        onClick={(e) => {
                            e.stopPropagation();
                            navigateToItem();
                        }}
                    >
                        {ctaLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SingleProductSection;
